import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { FileText, CheckCircle, XCircle, Download, Users, Upload as UploadIcon, Award, Building2, FolderOpen, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import UserManagement from '@/components/admin/UserManagement';
import CompanyManagement from '@/components/admin/CompanyManagement';
import InstructorInbox from '@/components/admin/InstructorInbox';
import SettingsManagement from '@/components/admin/SettingsManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function Admin({ user }) {
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [grade, setGrade] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('submissions');

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      return;
    }
    fetchSubmissions();
  }, [user]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions`, { withCredentials: true });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Erro ao carregar submissions');
    }
  };

  const handleUpdateSubmission = async (submissionId, status) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('status', status);
      if (grade) formData.append('grade', grade);
      if (feedback) formData.append('feedback', feedback);

      await axios.put(`${API}/admin/submissions/${submissionId}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Submission atualizada com sucesso!');
      setSelectedSubmission(null);
      setGrade('');
      setFeedback('');
      fetchSubmissions();
    } catch (error) {
      console.error('Error updating submission:', error);
      toast.error('Erro ao atualizar submission');
    } finally {
      setLoading(false);
    }
  };

  const openReviewModal = (submission) => {
    setSelectedSubmission(submission);
    setGrade(submission.grade || '');
    setFeedback(submission.feedback || '');
  };

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen">
        <Navigation user={user} />
        <div className="container mx-auto px-4 md:px-8 py-16">
          <Card className="p-8 rounded-none border border-border text-center">
            <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-2xl font-bold mb-2">Acesso Negado</h2>
            <p className="text-muted-foreground">Você não tem permissão para acessar esta área.</p>
          </Card>
        </div>
      </div>
    );
  }

  const pendingSubmissions = submissions.filter(s => s.status === 'pending');
  const reviewedSubmissions = submissions.filter(s => s.status !== 'pending');

  return (
    <div className="min-h-screen">
      <Navigation user={user} />

      <main className="container mx-auto px-4 md:px-8 py-16">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="admin-title">
              Painel Administrativo
            </h1>
            <p className="text-muted-foreground">Gerencie submissões, usuários e empresas</p>
          </div>
          <div className="flex gap-3">
            <Link to="/admin/modules">
              <Button variant="outline" className="rounded-none font-medium tracking-wide uppercase text-xs">
                Gerenciar Módulos
              </Button>
            </Link>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-border mb-8">
          {[
            { key: 'submissions', label: 'Submissões', icon: UploadIcon },
            { key: 'inbox', label: 'Inbox', icon: MessageSquare },
            { key: 'users', label: 'Usuários', icon: Users },
            ...(user?.role === 'super_admin' ? [{ key: 'companies', label: 'Empresas', icon: Building2 }] : []),
            ...(user?.role === 'admin' || user?.role === 'super_admin' ? [{ key: 'settings', label: 'Configurações', icon: Settings }] : [])
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium uppercase tracking-wide border-b-2 transition-colors ${activeTab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
            >
              <Icon className="h-4 w-4" strokeWidth={1.5} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && <UserManagement user={user} />}
        {activeTab === 'companies' && <CompanyManagement user={user} />}
        {activeTab === 'inbox' && <InstructorInbox user={user} />}
        {activeTab === 'settings' && <SettingsManagement user={user} />}
        {activeTab === 'submissions' && (<>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="p-4 rounded-none border border-border">\n            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-none">
                <UploadIcon className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingSubmissions.length}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Pendentes</p>
              </div>
            </div>
            </Card>
            <Card className="p-4 rounded-none border border-border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-none">
                  <Users className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{submissions.length}</p>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Total</p>
                </div>
              </div>
            </Card>
          </div>

          {pendingSubmissions.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Submissões Pendentes de Correção</h2>
              <Card className="rounded-none border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted border-b border-border">
                      <tr>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Aluno</th>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Módulo</th>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Arquivo</th>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Data</th>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {pendingSubmissions.map((submission) => (
                        <tr key={submission.submission_id} className="hover:bg-muted/30" data-testid={`submission-row-${submission.submission_id}`}>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium">{submission.user_name}</p>
                              <p className="text-xs text-muted-foreground">{submission.user_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-muted-foreground">{submission.module_id}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <FileText size={16} className="text-muted-foreground" />
                              <span>{submission.file_name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-muted-foreground">
                            {new Date(submission.submitted_at).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openReviewModal(submission)}
                                className="rounded-none"
                                data-testid={`review-btn-${submission.submission_id}`}
                              >
                                <Award className="h-4 w-4 mr-1" />
                                Avaliar
                              </Button>
                              <a
                                href={`${API}/submissions/${submission.submission_id}/download`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button variant="ghost" size="sm" className="rounded-none">
                                  <Download className="h-4 w-4" />
                                </Button>
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {reviewedSubmissions.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Submissões Avaliadas</h2>
              <Card className="rounded-none border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted border-b border-border">
                      <tr>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Aluno</th>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Módulo</th>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Arquivo</th>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Status</th>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Nota</th>
                        <th className="px-6 py-4 font-medium uppercase tracking-wide text-xs">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {reviewedSubmissions.map((submission) => (
                        <tr key={submission.submission_id} className="hover:bg-muted/30">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium">{submission.user_name}</p>
                              <p className="text-xs text-muted-foreground">{submission.user_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-muted-foreground">{submission.module_id}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <FileText size={16} className="text-muted-foreground" />
                              <span>{submission.file_name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-xs uppercase tracking-wide rounded-none ${submission.status === 'approved' ? 'bg-green-500/10 text-green-600 dark:text-green-400' :
                              'bg-red-500/10 text-red-600 dark:text-red-400'
                              }`}>
                              {submission.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-medium">
                            {submission.grade ? `${submission.grade}/100` : '-'}
                          </td>
                          <td className="px-6 py-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openReviewModal(submission)}
                              className="rounded-none"
                            >
                              Editar
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {submissions.length === 0 && (
            <Card className="p-12 rounded-none border border-border text-center">
              <UploadIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-bold mb-2">Nenhuma submissão encontrada</h3>
              <p className="text-muted-foreground">As submissões dos alunos aparecerão aqui</p>
            </Card>
          )}
        </>)}
      </main>

      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedSubmission(null)}>
          <Card className="max-w-2xl w-full p-8 rounded-none" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
            <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Avaliar Submissão</h2>

            <div className="space-y-4 mb-6">
              <div>
                <label className="text-sm font-medium mb-2 block">Aluno</label>
                <p className="text-base">{selectedSubmission.user_name}</p>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Arquivo</label>
                <div className="flex items-center gap-2">
                  <FileText size={16} />
                  <p className="text-base">{selectedSubmission.file_name}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Nota (0-100)</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  placeholder="Digite a nota"
                  className="rounded-none"
                  data-testid="grade-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Feedback</label>
                <Textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Escreva seu feedback para o aluno"
                  className="rounded-none min-h-[120px]"
                  data-testid="feedback-input"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={() => setSelectedSubmission(null)}
                className="flex-1 rounded-none font-medium tracking-wide uppercase text-xs"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleUpdateSubmission(selectedSubmission.submission_id, 'rejected')}
                className="flex-1 rounded-none font-medium tracking-wide uppercase text-xs bg-red-600 hover:bg-red-500"
                disabled={loading}
                data-testid="reject-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Rejeitar
              </Button>
              <Button
                onClick={() => handleUpdateSubmission(selectedSubmission.submission_id, 'approved')}
                className="flex-1 rounded-none font-medium tracking-wide uppercase text-xs bg-green-600 hover:bg-green-500"
                disabled={loading}
                data-testid="approve-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Aprovar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}